import React from 'react';
import { Activity, Heart, TrendingUp, BarChart3, LineChart } from 'lucide-react';
import {
  LineChart as RechartsLine, BarChart as RechartsBar, Line, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from 'recharts';

const tooltipStyle = { backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '8px' };
const axisStyle = { fontSize: '12px' };

const DiabetesCharts = ({ data }) => (
  <div className="space-y-6">
    <div className="bg-white p-6 rounded-lg border-2 border-teal-100">
      <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><LineChart className="w-5 h-5 text-teal-600" />Blood Sugar Trend</h4>
      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={data}>
          <defs><linearGradient id="bloodSugarGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#14b8a6" stopOpacity={0.8}/><stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="date" stroke="#6b7280" style={axisStyle} /><YAxis stroke="#6b7280" style={axisStyle} label={{ value: 'mg/dL', angle: -90, position: 'insideLeft' }} />
          <Tooltip contentStyle={tooltipStyle} labelStyle={{ fontWeight: 'bold', color: '#374151' }} />
          <Area type="monotone" dataKey="bloodSugar" stroke="#14b8a6" strokeWidth={3} fillOpacity={1} fill="url(#bloodSugarGradient)" name="Blood Sugar" />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-center gap-4 mt-2 text-xs text-gray-600">
        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-teal-500 rounded"></div> Blood Sugar</span>
        <span className="flex items-center gap-1"><div className="w-8 h-0.5 bg-red-500 border-dashed"></div> Target Range (70-180)</span>
      </div>
    </div>
    <div className="bg-white p-6 rounded-lg border-2 border-blue-100">
      <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-blue-600" />Insulin & Carbs</h4>
      <ResponsiveContainer width="100%" height={200}>
        <RechartsBar data={data}><CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="date" stroke="#6b7280" style={axisStyle} /><YAxis stroke="#6b7280" style={axisStyle} /><Tooltip contentStyle={tooltipStyle} /><Legend /><Bar dataKey="insulin" fill="#3b82f6" name="Insulin (units)" radius={[8, 8, 0, 0]} /><Bar dataKey="carbs" fill="#f59e0b" name="Carbs (g)" radius={[8, 8, 0, 0]} /></RechartsBar>
      </ResponsiveContainer>
    </div>
  </div>
);

const HypertensionCharts = ({ data }) => (
  <div className="space-y-6">
    <div className="bg-white p-6 rounded-lg border-2 border-red-100">
      <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><Heart className="w-5 h-5 text-red-600" />Blood Pressure Trend</h4>
      <ResponsiveContainer width="100%" height={250}>
        <RechartsLine data={data}><CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="date" stroke="#6b7280" style={axisStyle} /><YAxis stroke="#6b7280" style={axisStyle} label={{ value: 'mmHg', angle: -90, position: 'insideLeft' }} /><Tooltip contentStyle={tooltipStyle} /><Legend />
          <Line type="monotone" dataKey="systolic" stroke="#ef4444" strokeWidth={3} name="Systolic" dot={{ fill: '#ef4444', r: 4 }} />
          <Line type="monotone" dataKey="diastolic" stroke="#3b82f6" strokeWidth={3} name="Diastolic" dot={{ fill: '#3b82f6', r: 4 }} />
        </RechartsLine>
      </ResponsiveContainer>
    </div>
    <div className="bg-white p-6 rounded-lg border-2 border-pink-100">
      <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><Activity className="w-5 h-5 text-pink-600" />Heart Rate</h4>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data}>
          <defs><linearGradient id="heartRateGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ec4899" stopOpacity={0.8}/><stop offset="95%" stopColor="#ec4899" stopOpacity={0}/></linearGradient></defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="date" stroke="#6b7280" style={axisStyle} /><YAxis stroke="#6b7280" style={axisStyle} label={{ value: 'bpm', angle: -90, position: 'insideLeft' }} /><Tooltip />
          <Area type="monotone" dataKey="heartRate" stroke="#ec4899" strokeWidth={2} fillOpacity={1} fill="url(#heartRateGradient)" name="Heart Rate" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const AsthmaCharts = ({ data }) => (
  <div className="space-y-6">
    <div className="bg-white p-6 rounded-lg border-2 border-cyan-100">
      <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-cyan-600" />Inhaler Usage</h4>
      <ResponsiveContainer width="100%" height={200}><RechartsBar data={data}><CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="date" stroke="#6b7280" style={axisStyle} /><YAxis stroke="#6b7280" style={axisStyle} /><Tooltip /><Bar dataKey="puffs" fill="#06b6d4" name="Inhaler Puffs" radius={[8, 8, 0, 0]} /></RechartsBar></ResponsiveContainer>
    </div>
    <div className="bg-white p-6 rounded-lg border-2 border-blue-100">
      <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-blue-600" />Peak Flow</h4>
      <ResponsiveContainer width="100%" height={180}>
        <RechartsLine data={data.filter(d => d.peakFlow > 0)}><CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="date" stroke="#6b7280" style={axisStyle} /><YAxis stroke="#6b7280" style={axisStyle} label={{ value: 'L/min', angle: -90, position: 'insideLeft' }} /><Tooltip />
          <Line type="monotone" dataKey="peakFlow" stroke="#2563eb" strokeWidth={3} name="Peak Flow" dot={{ fill: '#2563eb', r: 4 }} />
        </RechartsLine>
      </ResponsiveContainer>
    </div>
  </div>
);

const ArthritisCharts = ({ data, t }) => (
  <div className="bg-white p-6 rounded-lg border-2 border-orange-100">
    <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><LineChart className="w-5 h-5 text-orange-600" />{t('chronicDisease.painLevelTrend')}</h4>
    <ResponsiveContainer width="100%" height={250}>
      <AreaChart data={data}>
        <defs><linearGradient id="painGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f97316" stopOpacity={0.8}/><stop offset="95%" stopColor="#f97316" stopOpacity={0}/></linearGradient></defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="date" stroke="#6b7280" style={axisStyle} /><YAxis stroke="#6b7280" style={axisStyle} domain={[0, 10]} label={{ value: 'Pain (1-10)', angle: -90, position: 'insideLeft' }} /><Tooltip />
        <Area type="monotone" dataKey="painLevel" stroke="#f97316" strokeWidth={3} fillOpacity={1} fill="url(#painGradient)" name="Pain Level" />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const IBSCharts = ({ data }) => (
  <div className="bg-white p-6 rounded-lg border-2 border-purple-100">
    <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2"><LineChart className="w-5 h-5 text-purple-600" />Symptom Severity & Stress</h4>
    <ResponsiveContainer width="100%" height={250}>
      <RechartsLine data={data}><CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" /><XAxis dataKey="date" stroke="#6b7280" style={axisStyle} /><YAxis stroke="#6b7280" style={axisStyle} domain={[0, 10]} /><Tooltip /><Legend />
        <Line type="monotone" dataKey="severity" stroke="#a855f7" strokeWidth={3} name="Symptom Severity" dot={{ fill: '#a855f7', r: 4 }} />
        <Line type="monotone" dataKey="stress" stroke="#ef4444" strokeWidth={3} name="Stress Level" dot={{ fill: '#ef4444', r: 4 }} />
      </RechartsLine>
    </ResponsiveContainer>
  </div>
);

const ConditionCharts = ({ activeCondition, chartData, t }) => {
  if (chartData.length === 0) return <p className="text-gray-500 text-center py-8">No data to display charts. Add more logs!</p>;
  switch (activeCondition) {
    case 'diabetes': return <DiabetesCharts data={chartData} />;
    case 'hypertension': return <HypertensionCharts data={chartData} />;
    case 'asthma': return <AsthmaCharts data={chartData} />;
    case 'arthritis': return <ArthritisCharts data={chartData} t={t} />;
    case 'ibs': return <IBSCharts data={chartData} />;
    default: return null;
  }
};

export default ConditionCharts;
